import { Component, inject, signal, computed, OnInit, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { InputTextModule } from 'primeng/inputtext';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { ToggleSwitchModule } from 'primeng/toggleswitch';

import { StateMasterService, StateMaster, StateMasterDto } from '../../services/state-master';
import { ToastService } from '../../services/toast';
import { PageTitleService } from '../../services/page-title';

@Component({
  selector: 'app-states',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    DialogModule,
    TableModule,
    ButtonModule,
    TagModule,
    TooltipModule,
    InputTextModule,
    IconFieldModule,
    InputIconModule,
    ToggleSwitchModule,
  ],
  templateUrl: './states.html',
  styleUrls: ['./states.css'],
})
export class StatesPage implements OnInit {
  private fb = inject(FormBuilder);
  private svc = inject(StateMasterService);
  private toast = inject(ToastService);
  readonly pageTitle = inject(PageTitleService).title;
  private platformId = inject(PLATFORM_ID);

  loading = signal(false);
  saving = signal(false);
  editing = signal(false);
  showModal = signal(false);
  search = signal('');
  rows = signal<StateMaster[]>([]);

  filtered = computed(() => {
    const q = this.search().trim().toLowerCase();
    if (!q) return this.rows();
    return this.rows().filter((r) => (r.stateName || '').toLowerCase().includes(q));
  });

  form: FormGroup = this.fb.group({
    id: [0],
    stateName: ['', Validators.required],
    isActive: [true],
  });

  ngOnInit() {
    if (isPlatformBrowser(this.platformId)) this.load();
  }

  load() {
    this.loading.set(true);
    this.svc.getAll().subscribe({
      next: (res: any) => {
        this.loading.set(false);
        const data = res?.data ?? res ?? [];
        this.rows.set(Array.isArray(data) ? data : []);
      },
      error: () => this.loading.set(false),
    });
  }

  openAdd() {
    this.editing.set(false);
    this.form.reset({ id: 0, stateName: '', isActive: true });
    this.showModal.set(true);
  }

  openEdit(r: StateMaster) {
    this.editing.set(true);
    this.form.reset({ id: r.id, stateName: r.stateName ?? '', isActive: r.isActive ?? true });
    this.showModal.set(true);
  }

  closeModal() {
    this.showModal.set(false);
  }

  save() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.toast.warning('State name is required.');
      return;
    }
    const v = this.form.getRawValue();
    const dto: StateMasterDto = {
      id: this.editing() ? Number(v.id) : 0,
      stateName: (v.stateName || '').trim(),
      isActive: !!v.isActive,
    };
    this.saving.set(true);
    const req = this.editing() ? this.svc.update(dto) : this.svc.insert(dto);
    req.subscribe({
      next: (res: any) => {
        this.saving.set(false);
        if (res?.success === false) {
          this.toast.error(res?.message || 'Failed to save state');
          return;
        }
        this.toast.success(this.editing() ? 'State updated' : 'State created');
        this.closeModal();
        this.load();
      },
      error: () => this.saving.set(false),
    });
  }

  toggleActive(r: StateMaster) {
    const next = !r.isActive;
    this.patchRow(r.id, { isActive: next });
    this.svc.setActive(r.id, next).subscribe({
      next: (res: any) => {
        if (res?.success === false) {
          this.patchRow(r.id, { isActive: r.isActive });
          this.toast.error(res?.message || 'Could not update status.');
        }
      },
      error: () => {
        this.patchRow(r.id, { isActive: r.isActive });
        this.toast.error('Could not update status.');
      },
    });
  }

  private patchRow(id: number, patch: Partial<StateMaster>) {
    this.rows.update((list) => list.map((x) => (x.id === id ? { ...x, ...patch } : x)));
  }

  async remove(r: StateMaster) {
    const ok = await this.toast.confirm(`"${r.stateName}" will be deleted.`, 'Delete state?', 'Delete');
    if (!ok) return;
    this.svc.delete(r.id).subscribe({
      next: (res: any) => {
        if (res?.success === false) {
          this.toast.error(res?.message || 'Failed to delete');
          return;
        }
        this.toast.success('State deleted');
        this.load();
      },
    });
  }
}
